/**
 * Split rendered markdown HTML into text, transcript citation ([N], [N-M], 【N】) and
 * source citation ([Sn]) tokens. Adjacent transcript markers collapse into one token,
 * so a run like [3][7][12] becomes a single icon.
 */

const touches = (a, b) => a.start <= b.end + 1 && b.start <= a.end + 1;

const chunksOf = (token) => {
  if (token.chunks?.length) return token.chunks;
  const out = [];
  for (let n = token.start; n <= token.end; n += 1) out.push(n);
  return out;
};

const transcriptToken = (chunks) => {
  const sorted = [...new Set(chunks)].sort((a, b) => a - b);
  return {
    type: 'transcript',
    start: sorted[0],
    end: sorted[sorted.length - 1],
    chunks: sorted,
  };
};

const mergeTokens = (a, b) => transcriptToken([...chunksOf(a), ...chunksOf(b)]);

const rangeToken = (nums) => ({ type: 'transcript', start: nums[0], end: nums[nums.length - 1] });

/** Push `token`, folding it into the previous transcript token when their ranges touch. */
const appendOrMerge = (tokens, token) => {
  const last = tokens[tokens.length - 1];
  if (last?.type === 'transcript' && touches(last, token)) {
    tokens[tokens.length - 1] = mergeTokens(last, token);
  } else {
    tokens.push(token);
  }
};

/** Consecutive numbers ([4][5][6]) and touching ranges become one range; a repeated [12][12] counts once. */
const mergeConsecutiveCitations = (parts) => {
  const merged = [];
  let pendingNums = [];

  const flushPending = () => {
    if (pendingNums.length === 0) return;
    appendOrMerge(merged, rangeToken(pendingNums));
    pendingNums = [];
  };

  for (const part of parts) {
    if (part.type !== 'transcript') {
      flushPending();
      merged.push(part);
    } else if (part.start === part.end) {
      const num = part.start;
      const lastPending = pendingNums[pendingNums.length - 1];
      if (pendingNums.length === 0 || num === lastPending + 1) {
        pendingNums.push(num);
      } else if (num !== lastPending) {
        flushPending();
        pendingNums.push(num);
      }
    } else if (pendingNums.length > 0) {
      const pending = rangeToken(pendingNums);
      pendingNums = [];
      if (touches(pending, part)) {
        appendOrMerge(merged, mergeTokens(pending, part));
      } else {
        appendOrMerge(merged, pending);
        appendOrMerge(merged, part);
      }
    } else {
      appendOrMerge(merged, part);
    }
  }
  flushPending();
  return merged;
};

/**
 * One icon per run of adjacent markers in text, e.g. [3][7][12] or [1] [2] [3].
 * (LLM often cites non-consecutive sentences side by side.)
 */
const consolidateAdjacentTranscriptCitations = (tokens) => {
  const result = [];
  let i = 0;

  while (i < tokens.length) {
    const token = tokens[i];
    if (token.type !== 'transcript') {
      result.push(token);
      i += 1;
      continue;
    }

    let current = transcriptToken(chunksOf(token));
    let j = i + 1;
    while (j < tokens.length) {
      if (tokens[j].type === 'transcript') {
        current = mergeTokens(current, tokens[j]);
        j += 1;
        continue;
      }
      if (tokens[j].type === 'text' && /^\s*$/.test(tokens[j].content) && tokens[j + 1]?.type === 'transcript') {
        j += 1;
        continue;
      }
      break;
    }

    result.push(current);
    i = j;
  }

  return result;
};

/**
 * Bring the marker spellings models produce to one form: [12, 13-14] → [12][13-14]
 * (as the backend's _COMBINED_CIT), and with sources (S13, S14) / bare S9 → [S13] [S14] / [S9].
 */
const normalizeCitationMarkers = (html, withSources) => {
  let normalized = html.replace(/\s*\[transcript\]/gi, '');
  normalized = normalized.replace(
    /\[(\d+(?:\s*-\s*\d+)?(?:\s*,\s*\d+(?:\s*-\s*\d+)?)+)\]/g,
    (_, inner) => inner.split(/\s*,\s*/).map((p) => `[${p.trim()}]`).join('')
  );
  if (withSources) {
    normalized = normalized.replace(/\(S\d+(?:\s*,\s*S\d+)*\)/g, (match) => (
      [...match.matchAll(/S(\d+)/g)].map((m) => `[S${m[1]}]`).join(' ')
    ));
    normalized = normalized.replace(/(?<!\[)\bS(\d+)\b(?!\])/g, '[S$1]');
  }
  return normalized;
};

const CITATION_SPLIT = /((?:\[\d+(?:-\d+)?\]|【\d+(?:-\d+)?】|\[S\d+\]))/g;

const tokenOf = (part) => {
  const srcMatch = part.match(/^\[S(\d+)\]$/);
  if (srcMatch) return { type: 'source', num: parseInt(srcMatch[1], 10), raw: part };

  const rangeMatch = part.match(/^(?:\[(\d+)-(\d+)\]|【(\d+)-(\d+)】)$/);
  if (rangeMatch) {
    const start = parseInt(rangeMatch[1] || rangeMatch[3], 10);
    const end = parseInt(rangeMatch[2] || rangeMatch[4], 10);
    return { type: 'transcript', start, end, raw: part };
  }

  const singleMatch = part.match(/^(?:\[(\d+)\]|【(\d+)】)$/);
  if (singleMatch) {
    const num = parseInt(singleMatch[1] || singleMatch[2], 10);
    return { type: 'transcript', start: num, end: num, raw: part };
  }

  return { type: 'text', content: part };
};

export function tokenizeCitations(html, { withSources = false } = {}) {
  const tokens = normalizeCitationMarkers(html, withSources)
    .split(CITATION_SPLIT)
    .filter(Boolean)
    .map(tokenOf);
  return consolidateAdjacentTranscriptCitations(mergeConsecutiveCitations(tokens));
}

export const transcriptChunksOf = chunksOf;
